import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import HelpButton from '../tours/HelpButton';
import useAutoTour from '../tours/useAutoTour';
import { useWorkspaceRoster, useWorkspaceRosterMap } from '../hooks/useWorkspaceRoster';
import projectManager from '../models/projects';
import CreateTaskModal from './modals/CreateTaskModal';
import CreateProjectModal from './ProjectManagement/CreateProjectModal';
import CreateSprintModal from './ProjectManagement/CreateSprintModal';
import ConfirmDeleteModal from './shared/ConfirmDeleteModal';
import TaskDetailPanel from './ProjectManagement/TaskDetailPanel';
import TimelineView from './ProjectManagement/TimelineView';
import ProjectFinancials from './Projects/ProjectFinancials';
import DocumentImporter from './Projects/DocumentImporter';
import ProjectTimeTab from './TimeTracking/ProjectTimeTab';
import WorkflowEditor from './WorkflowEditor';
import { api } from '../api/client';

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY   = '#0C2D48';
const BORDER = '#e0e0e0';
const BG     = '#f8f9fb';
const ACCENT = '#5352ed';
const GREEN  = '#27ae60';
const RED    = '#E96A6A';
const YELLOW = '#ffa502';
const FONT   = "'Raleway', sans-serif";

const PRIORITY_COLOR = { Low: '#7bed9f', Medium: '#ffa502', High: '#ff4757', Critical: '#ff2d55' };
const STATUS_COLOR   = { todo: '#a4b0be', 'in progress': ACCENT, review: '#f9ca24', completed: GREEN };
const PROJECT_STATUS_COLOR = { active: GREEN, completed: '#a4b0be', 'on hold': YELLOW };

// ── Shared atoms ──────────────────────────────────────────────────────────────

function Avatar({ userId, size = 26 }) {
  // Resolves from the shared roster cache — no per-avatar fetch, no mock lookup.
  const rosterMap = useWorkspaceRosterMap();
  const u = rosterMap.get(userId);
  // Unresolved id (e.g. a removed member) → neutral placeholder, never a crash.
  const name = u?.name || 'Former member';
  const initials = u ? (u.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <div title={name} style={{
      width: size, height: size, borderRadius: '50%',
      background: u?.color || NAVY, color: '#fff',
      fontSize: size * 0.38, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, border: '2px solid #fff', marginLeft: size > 24 ? -6 : 0,
      fontFamily: "'Poppins', sans-serif",
    }}>
      {initials}
    </div>
  );
}

function PriorityBadge({ priority }) {
  const color = PRIORITY_COLOR[priority] || '#ddd';
  return (
    <span style={{
      background: `${color}28`, color, border: `1px solid ${color}50`,
      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    }}>
      {priority}
    </span>
  );
}

function StatusBadge({ status, onChange }) {
  const color = STATUS_COLOR[status] || '#ddd';
  const style = {
    background: `${color}28`, color, border: `1px solid ${color}50`,
    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    textTransform: 'capitalize',
  };
  if (!onChange) return <span style={style}>{status}</span>;
  return (
    <select value={status} onChange={e => { e.stopPropagation(); onChange(e.target.value); }}
      onClick={e => e.stopPropagation()}
      style={{ ...style, cursor: 'pointer', outline: 'none' }}>
      {['todo', 'in progress', 'review', 'completed'].map(s => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
}

function ProjectStatusBadge({ status }) {
  const color = PROJECT_STATUS_COLOR[status] || '#aaa';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color,
      background: `${color}18`, border: `1px solid ${color}40`,
      padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: 6, borderRadius: 3, background: BORDER, overflow: 'hidden', flex: 1 }}>
      <div style={{
        height: '100%', borderRadius: 3,
        background: color || NAVY,
        width: `${Math.min(Math.max(pct, 0), 100)}%`,
        transition: 'width 0.3s',
      }} />
    </div>
  );
}

function formatTime(minutes) {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? `${m}m` : ''}` : `${m}m`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROJECT_STATUS_SORT = { active: 0, 'on hold': 1, completed: 2 };

function sortProjects(projects, sortBy) {
  const list = [...projects];
  if (sortBy === 'name-asc')  return list.sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === 'deadline')  return list.sort((a, b) => (a.deadline || '9999') < (b.deadline || '9999') ? -1 : 1);
  if (sortBy === 'status')    return list.sort((a, b) => (PROJECT_STATUS_SORT[a.status] ?? 3) - (PROJECT_STATUS_SORT[b.status] ?? 3));
  if (sortBy === 'updated')   return list.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────

function TabBar({ openTabs, activeId, onSelect, onClose, onNew, isMobile }) {
  const allTabs = [
    { id: 'portfolio', name: 'Projects', closable: false },
    ...openTabs.map(t => ({ id: String(t.id), name: t.name, closable: true })),
  ];

  return (
    <div style={{
      background: '#fff', borderBottom: `1px solid #d1d5db`,
      display: 'flex', alignItems: 'flex-end',
      padding: '6px 12px 0', flexShrink: 0, gap: 2,
    }}>
      {allTabs.map(tab => {
        const isActive = activeId === tab.id;
        const label = tab.name.length > 20 ? tab.name.slice(0, 20) + '…' : tab.name;
        return (
          <div
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '7px 10px',
              background: isActive ? '#fff' : '#f3f4f6',
              borderTop: `1px solid ${isActive ? '#d1d5db' : 'transparent'}`,
              borderLeft: `1px solid ${isActive ? '#d1d5db' : 'transparent'}`,
              borderRight: `1px solid ${isActive ? '#d1d5db' : 'transparent'}`,
              borderBottom: isActive ? '1px solid #fff' : 'none',
              borderRadius: '8px 8px 0 0',
              marginBottom: isActive ? -1 : 0,
              cursor: 'pointer', position: 'relative',
              zIndex: isActive ? 2 : 1, flexShrink: 0,
            }}
          >
            <span style={{
              fontSize: 13, fontWeight: isActive ? 600 : 500,
              color: isActive ? NAVY : '#6b7280',
              whiteSpace: 'nowrap', userSelect: 'none',
            }}>
              {label}
            </span>
            {tab.closable && (
              <button
                onClick={e => { e.stopPropagation(); onClose(tab.id); }}
                title="Close tab"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', fontSize: 15, padding: '0 2px',
                  lineHeight: 1, borderRadius: 3, marginLeft: 2,
                  display: 'flex', alignItems: 'center',
                }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* + New Project — hidden on mobile at portfolio view (PortfolioLanding has its own button) */}
      {!(isMobile && activeId === 'portfolio') && (
        <div style={{ paddingBottom: 6, flexShrink: 0 }}>
          <button
            onClick={onNew}
            style={{
              padding: '6px 14px', borderRadius: 7, border: 'none',
              background: NAVY, color: '#fff', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? '+' : '+ New Project'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────

function StatsBar({ projects, isMobile }) {
  const allTasks = useMemo(() => projectManager.getAllTasks(), []);
  const stats = useMemo(() => {
    const active      = projects.filter(p => p.status === 'active').length;
    const outstanding = allTasks.filter(t => t.status !== 'completed').length;
    const overdue     = allTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
    const hoursLogged = Math.round(
      allTasks.reduce((s, t) => s + ((t.timeTracked || []).reduce((a, e) => a + e.minutes, 0)), 0) / 60 * 10
    ) / 10;
    return [
      { label: 'Active Projects',   value: active,                    color: GREEN  },
      { label: 'Outstanding Tasks', value: outstanding,               color: NAVY   },
      { label: 'Overdue',           value: overdue, color: overdue > 0 ? RED : '#aaa' },
      { label: 'Hours Logged',      value: `${hoursLogged}h`,         color: ACCENT },
    ];
  }, [projects, allTasks]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: 8, padding: '10px 16px',
      background: '#fff', borderBottom: `1px solid ${BORDER}`,
      flexShrink: 0,
    }}>
      {stats.map(s => (
        <div key={s.label} style={{
          background: BG, border: `1px solid ${BORDER}`,
          borderRadius: 8, padding: '8px 12px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value}</div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 2, fontFamily: FONT }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProjectTracker({ onCreateProjectInvoice, onNavigate }) {
  const { currentUser, activeWorkspaceId } = useCurrentUser();

  // Data
  const [projects, setProjects]   = useState([]);
  const [tasks, setTasks]         = useState([]);
  const [sprints, setSprints]     = useState([]);
  const [sections, setSections]   = useState([]);

  // Selection
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSprint, setSelectedSprint]   = useState(null);

  // Landing view: 'grid' | 'list'
  const [landingView, setLandingView] = useState(() =>
    localStorage.getItem('project-landing-view') || 'grid'
  );

  // Project tabs: 'overview' | 'workflow' | 'tasks' | 'files' | 'time' | 'finances' | 'notes'
  const [activeTab, setActiveTab] = useState('tasks');

  // Workflow stages/columns for the active project
  const [projectWorkflow, setProjectWorkflow] = useState({ stages: [], columns: [] });

  // Task view inside Tasks tab: 'list' | 'kanban' | 'priority'
  const [taskView, setTaskView] = useState(() =>
    localStorage.getItem('project-task-view') || 'list'
  );

  // Calendar
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });

  // Modals
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editProject, setEditProject]             = useState(null);
  const [showCreateSprint, setShowCreateSprint]   = useState(false);
  const [editSprint, setEditSprint]               = useState(null);
  const [showCreateTask, setShowCreateTask]       = useState(false);
  const [showImporter, setShowImporter]           = useState(false);

  // Detail panel
  const [openTaskId, setOpenTaskId] = useState(null);

  // Drag and drop
  const [draggingId, setDraggingId] = useState(null);

  // Task filters
  const [searchText, setSearchText]         = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  // Open project browser tabs (UI only, reset on reload)
  const [openProjectTabs, setOpenProjectTabs] = useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  // Section management
  const [newSectionName, setNewSectionName]   = useState('');
  const [showAddSection, setShowAddSection]   = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try { await projectManager.refreshProjects(); } catch (_) {}
    projectManager.updateAllTaskPriorities();
    const allProjects = projectManager.getAllProjects();
    setProjects(allProjects);

    if (selectedProject) {
      const updated = allProjects.find(p => p.id === selectedProject.id) || null;
      setSelectedProject(updated);
      if (updated) {
        try { await projectManager.refreshProjectSubresources(updated.id); } catch (_) {}
        setTasks(projectManager.getTasksByProject(updated.id));
        setSprints(projectManager.getSprintsByProject(updated.id));
        setSections(projectManager.getSectionsByProject(updated.id));
      }
    } else {
      setTasks(projectManager.getTasksByUser(currentUser?.id || 1));
      setSprints([]);
      setSections([]);
    }
  }, [selectedProject, currentUser]);

  useEffect(() => {
    setSelectedProject(null);
    setOpenProjectTabs([]);
    loadData();
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleTabSelect = (id) => {
    if (id === 'portfolio') {
      handleBackToLanding();
    } else {
      const p = projectManager.getProject(parseInt(id, 10));
      if (p) handleSelectProject(p);
    }
  };

  const handleSelectProject = async (project) => {
    const fresh = projectManager.getProject(project.id);
    setSelectedProject(fresh);
    setSelectedSprint(null);
    setActiveTab('tasks');
    setSearchText('');
    setFilterPriority('');
    setFilterAssignee('');
    setProjectWorkflow({ stages: [], columns: [] });
    // Add to open tabs if not already present
    setOpenProjectTabs(prev => {
      if (prev.some(t => t.id === project.id)) return prev;
      return [...prev, { id: project.id, name: project.name, type: project.projectType || project.project_type }];
    });
    try { await projectManager.refreshProjectSubresources(project.id); } catch (_) {}
    setTasks(projectManager.getTasksByProject(project.id));
    setSprints(projectManager.getSprintsByProject(project.id));
    setSections(projectManager.getSectionsByProject(project.id));
    fetchProjectWorkflow(project.id);
  };

  const handleCloseTab = (id) => {
    const numId = parseInt(id, 10);
    setOpenProjectTabs(prev => prev.filter(t => t.id !== numId));
    if (selectedProject?.id === numId) handleBackToLanding();
  };

  const handleBackToLanding = () => {
    setSelectedProject(null);
    setSelectedSprint(null);
    setTasks(projectManager.getTasksByUser(currentUser?.id || 1));
    setSprints([]);
    setSections([]);
  };

  const handleSelectSprint = (sprint) => {
    setSelectedSprint(sprint);
    setTasks(projectManager.getTasksBySprint(sprint.id));
  };

  // ── Filtered tasks ────────────────────────────────────────────────────────

  const filteredTasks = useMemo(() => tasks.filter(t => {
    if (searchText && !t.title.toLowerCase().includes(searchText.toLowerCase())) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterAssignee && !(t.assignedTo || []).includes(parseInt(filterAssignee))) return false;
    return true;
  }), [tasks, searchText, filterPriority, filterAssignee]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const fetchProjectWorkflow = async (projectId) => {
    try {
      const r = await api(`/api/projects/${projectId}/workflow`);
      if (r.ok) {
        const d = await r.json();
        setProjectWorkflow({ stages: d.stages || [], columns: d.columns || [] });
      }
    } catch (err) {
      console.error('[ProjectTracker] fetchProjectWorkflow failed:', err);
    }
  };

  const handleCreateProject = async (data) => {
    try {
      if (editProject) {
        await projectManager.updateProject(editProject.id, data);
      } else {
        const created = await projectManager.createProject(data);
        // Post workflow stages/columns if provided
        const draft = data.workflowDraft || {};
        if (created?.id && (draft.stages?.length || draft.columns?.length)) {
          const pid = created.id;
          for (const stage of (draft.stages || [])) {
            await api(`/api/projects/${pid}/stages`, {
              method: 'POST',
              body: JSON.stringify({
                name: stage.name, colour: stage.colour || '#94a3b8',
                position: stage.position ?? 0,
                isTerminal: stage.isTerminal ?? false,
                isStart: stage.isStart ?? false,
              }),
            });
          }
          for (const col of (draft.columns || [])) {
            await api(`/api/projects/${pid}/columns`, {
              method: 'POST',
              body: JSON.stringify({
                name: col.name, fieldKey: col.fieldKey,
                fieldType: col.fieldType || 'TEXT',
                options: col.options ?? null,
                position: col.position ?? 0,
                isRequired: col.isRequired ?? false,
              }),
            });
          }
        }
      }
      setEditProject(null);
      await loadData();
    } catch (err) { console.error('Failed to save project:', err); }
  };

  const handleDeleteProject = async (id) => {
    try {
      await projectManager.deleteProject(id);
      if (selectedProject?.id === id) handleBackToLanding();
      await loadData();
    } catch (err) { console.error('Failed to delete project:', err); }
  };

  const handleCreateSprint = async (data) => {
    try {
      if (editSprint) projectManager.updateSprint(editSprint.id, data);
      else await projectManager.createSprint(data);
    } catch (err) { console.error('Failed to save sprint:', err); }
    setEditSprint(null);
    loadData();
  };

  const handleCreateTask = async (data) => {
    try { await projectManager.createProjectTask(data); }
    catch (err) { console.error('Failed to create task:', err); }
    loadData();
  };

  const handleTaskStatusChange = (taskId, newStatus) => {
    projectManager.updateProjectTask(taskId, { status: newStatus });
    loadData();
  };

  const handleAddSection = async () => {
    const name = newSectionName.trim();
    if (!name || !selectedProject) return;
    try { await projectManager.createSection(selectedProject.id, name); }
    catch (err) { console.error('Failed to create section:', err); }
    setNewSectionName('');
    setShowAddSection(false);
    loadData();
  };

  const handleDragStart = (e, taskId) => {
    setDraggingId(taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, col) => {
    e.preventDefault();
    if (!draggingId) return;
    const originalTask = tasks.find(t => String(t.id) === String(draggingId));
    const originalStageId = originalTask?.stageId ?? null;
    const patch = typeof col === 'object'
      ? { stageId: col.id }                         // dynamic stage
      : { status: col };                            // legacy status string
    projectManager.updateProjectTask(draggingId, patch);
    setDraggingId(null);
    loadData();
    if (patch.stageId) {
      api(`/api/tasks/${draggingId}`, {
        method: 'PUT',
        body: JSON.stringify({ stageId: col.id }),
      }).catch(err => {
        console.error('[handleDrop] stageId PUT failed:', err);
        projectManager.updateProjectTask(draggingId, { stageId: originalStageId });
        loadData();
      });
    }
  };

  const { members: allUsers } = useWorkspaceRoster();

  const setTaskViewPersist = (v) => {
    setTaskView(v);
    localStorage.setItem('project-task-view', v);
  };

  const setLandingViewPersist = (v) => {
    setLandingView(v);
    localStorage.setItem('project-landing-view', v);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="project-tracker-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: BG, fontFamily: FONT }}>

      <TabBar
        openTabs={openProjectTabs}
        activeId={selectedProject ? String(selectedProject.id) : 'portfolio'}
        onSelect={handleTabSelect}
        onClose={handleCloseTab}
        onNew={() => { setEditProject(null); setShowCreateProject(true); }}
        isMobile={isMobile}
      />
      <div data-tour-id="projects-stats"><StatsBar projects={projects} isMobile={isMobile} /></div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ── LANDING: Portfolio card / list grid ── */}
        {!selectedProject && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <PortfolioLanding
              projects={projects}
              landingView={landingView}
              onSetLandingView={setLandingViewPersist}
              onSelectProject={handleSelectProject}
              onNewProject={() => { setEditProject(null); setShowCreateProject(true); }}
              onImportDocument={() => setShowImporter(true)}
              onNavigate={onNavigate}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* ── PROJECT VIEW: tabbed ── */}
        {selectedProject && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Project header */}
            <div style={{
              background: '#fff', borderBottom: 'none',
              padding: '14px 24px 0',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {/* Top row: back, dot, title, status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={handleBackToLanding}
                    style={{
                      background: 'none', border: `1px solid ${BORDER}`, borderRadius: 7,
                      padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#666',
                      fontFamily: FONT, flexShrink: 0,
                    }}
                  >
                    ← Projects
                  </button>
                  <div style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: selectedProject.color || ACCENT, flexShrink: 0,
                  }} />
                  <h2 style={{
                    margin: 0, fontFamily: "'Poppins', sans-serif",
                    fontSize: 18, fontWeight: 800, color: NAVY, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {selectedProject.name}
                  </h2>
                  <ProjectStatusBadge status={selectedProject.status} />
                </div>

                {/* Bottom row: client + actions (left-aligned) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {selectedProject.client && (
                    <span style={{ fontSize: 13, color: '#888', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedProject.client}
                    </span>
                  )}
                  <button
                    onClick={() => { setEditProject(selectedProject); setShowCreateProject(true); }}
                    style={{
                      padding: '6px 12px', borderRadius: 7, border: `1px solid ${BORDER}`,
                      background: '#fff', color: NAVY, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', fontFamily: FONT,
                    }}
                  >
                    Edit
                  </button>
                  {onCreateProjectInvoice && (
                    <button
                      onClick={() => onCreateProjectInvoice(selectedProject.id)}
                      style={{
                        padding: '6px 12px', borderRadius: 7,
                        border: `1px solid ${ACCENT}`, background: '#fff', color: ACCENT,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
                      }}
                    >
                      £ Invoice
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm({ id: selectedProject.id, name: selectedProject.name })}
                    title="Delete project"
                    style={{
                      padding: '6px 10px', borderRadius: 7, border: `1px solid ${BORDER}`,
                      background: '#fff', color: RED, fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    🗑
                  </button>
                </div>
              </div>

              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb' }}>
                {[
                  { key: 'overview',  label: 'Overview'     },
                  { key: 'workflow',  label: '⚙ Workflow' },
                  { key: 'tasks',     label: 'Tasks'        },
                  { key: 'files',     label: 'Files'     },
                  { key: 'time',      label: 'Time'      },
                  { key: 'finances',  label: 'Finances'  },
                  { key: 'notes',     label: 'Notes'     },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    onMouseEnter={e => {
                      if (activeTab !== tab.key) {
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.borderBottomColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== tab.key) {
                        e.currentTarget.style.color = '#6b7280';
                        e.currentTarget.style.borderBottomColor = 'transparent';
                      }
                    }}
                    style={{
                      padding: '10px 18px',
                      background: 'none',
                      border: 'none',
                      borderBottom: activeTab === tab.key ? '2px solid #7c9a3a' : '2px solid transparent',
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: 14,
                      fontWeight: activeTab === tab.key ? 600 : 500,
                      color: activeTab === tab.key ? '#7c9a3a' : '#6b7280',
                      cursor: 'pointer',
                      marginBottom: -2,
                      transition: 'all 0.15s',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Tab content ── */}
            <div style={{ flex: 1, overflowY: 'auto' }}>

              {/* OVERVIEW */}
              {activeTab === 'overview' && (
                <OverviewTab
                  project={selectedProject}
                  tasks={tasks}
                  allUsers={allUsers}
                  onEdit={() => { setEditProject(selectedProject); setShowCreateProject(true); }}
                />
              )}

              {/* TASKS */}
              {activeTab === 'tasks' && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Tasks toolbar */}
                  <div style={{
                    background: '#fff', borderBottom: `1px solid ${BORDER}`,
                    padding: '10px 24px',
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  }}>
                    {/* Search */}
                    <input
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      placeholder="Search tasks…"
                      style={{
                        padding: '6px 12px', borderRadius: 20,
                        border: `1px solid ${BORDER}`, fontSize: 12, outline: 'none',
                        width: 150, fontFamily: FONT,
                      }}
                    />

                    {/* Priority filter */}
                    <select
                      value={filterPriority}
                      onChange={e => setFilterPriority(e.target.value)}
                      style={{
                        padding: '6px 10px', borderRadius: 20,
                        border: `1px solid ${BORDER}`, fontSize: 12, outline: 'none',
                        background: '#fff', cursor: 'pointer', fontFamily: FONT,
                      }}
                    >
                      <option value="">All priorities</option>
                      <option value="Critical">Critical</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>

                    {/* Assignee filter */}
                    <select
                      value={filterAssignee}
                      onChange={e => setFilterAssignee(e.target.value)}
                      style={{
                        padding: '6px 10px', borderRadius: 20,
                        border: `1px solid ${BORDER}`, fontSize: 12, outline: 'none',
                        background: '#fff', cursor: 'pointer', fontFamily: FONT,
                      }}
                    >
                      <option value="">All members</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>

                    {/* Spacer */}
                    <div style={{ flex: 1 }} />

                    {/* Edit workflow shortcut */}
                    <button
                      onClick={() => setActiveTab('workflow')}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#6b7280', fontSize: 12, fontFamily: FONT,
                        padding: '4px 2px', textDecoration: 'underline', flexShrink: 0,
                      }}
                      title="Go to Workflow tab"
                    >
                      Edit workflow →
                    </button>

                    {/* View toggle: List | Kanban | Priority */}
                    <div style={{
                      display: 'flex', background: BG, borderRadius: 8,
                      border: `1px solid ${BORDER}`, overflow: 'hidden',
                    }}>
                      {[
                        { key: 'list',     label: '≡ List'     },
                        { key: 'kanban',   label: '⬛ Kanban'  },
                        { key: 'priority', label: '⚑ Priority' },
                      ].map(v => (
                        <button
                          key={v.key}
                          onClick={() => setTaskViewPersist(v.key)}
                          style={{
                            padding: '6px 12px',
                            background: taskView === v.key ? NAVY : 'transparent',
                            color: taskView === v.key ? '#fff' : '#888',
                            border: 'none',
                            borderRight: v.key !== 'priority' ? `1px solid ${BORDER}` : 'none',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: taskView === v.key ? 700 : 400,
                            fontFamily: FONT,
                          }}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>

                    {/* Add Task */}
                    <button
                      onClick={() => setShowCreateTask(true)}
                      style={{
                        padding: '7px 16px', borderRadius: 8,
                        border: 'none', background: ACCENT, color: '#fff',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                      }}
                    >
                      + Add Task
                    </button>
                  </div>

                  {/* Task view content */}
                  <div style={{ flex: 1, overflowY: taskView === 'kanban' ? 'hidden' : 'auto', padding: taskView === 'kanban' ? 0 : '20px 24px' }}>
                    {taskView === 'list' && (
                      <TaskListView
                        tasks={filteredTasks}
                        sections={sections}
                        selectedProject={selectedProject}
                        allUsers={allUsers}
                        onTaskClick={setOpenTaskId}
                        onStatusChange={handleTaskStatusChange}
                        onAddSection={handleAddSection}
                        showAddSection={showAddSection}
                        setShowAddSection={setShowAddSection}
                        newSectionName={newSectionName}
                        setNewSectionName={setNewSectionName}
                      />
                    )}
                    {taskView === 'kanban' && (
                      <KanbanView
                        tasks={filteredTasks}
                        stages={projectWorkflow.stages}
                        allUsers={allUsers}
                        onTaskClick={setOpenTaskId}
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        draggingId={draggingId}
                      />
                    )}
                    {taskView === 'priority' && (
                      <PriorityView
                        tasks={filteredTasks}
                        allUsers={allUsers}
                        onTaskClick={setOpenTaskId}
                        onStatusChange={handleTaskStatusChange}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* WORKFLOW */}
              {activeTab === 'workflow' && (
                <div style={{ padding: '20px 24px' }}>
                  <div style={{
                    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                    padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e',
                    fontFamily: FONT,
                  }}>
                    ⚠ Changes to stages here affect this project only. Existing tasks on removed stages will need reassigning.
                  </div>
                  <WorkflowEditor
                    mode="project"
                    entityId={selectedProject.id}
                    readOnly={false}
                    showColumns={true}
                    showAutomations={true}
                    showSaveAsTemplate={true}
                    onSaved={() => fetchProjectWorkflow(selectedProject.id)}
                  />
                </div>
              )}

              {/* FILES */}
              {activeTab === 'files' && <FilesTab project={selectedProject} />}

              {/* TIME */}
              {activeTab === 'time' && <ProjectTimeTab project={selectedProject} />}

              {/* FINANCES */}
              {activeTab === 'finances' && (
                <ProjectFinancials
                  projectId={selectedProject.id}
                  onBack={() => setActiveTab('tasks')}
                />
              )}

              {/* NOTES */}
              {activeTab === 'notes' && <NotesTab project={selectedProject} />}

            </div>
          </div>
        )}
      </div>

      {/* ── Panels & Modals ── */}
      {openTaskId && (
        <TaskDetailPanel
          taskId={openTaskId}
          onClose={() => { setOpenTaskId(null); loadData(); }}
          onUpdated={() => loadData()}
        />
      )}

      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={() => { setShowCreateProject(false); setEditProject(null); }}
        onSave={handleCreateProject}
        editProject={editProject}
      />

      <CreateSprintModal
        isOpen={showCreateSprint}
        onClose={() => { setShowCreateSprint(false); setEditSprint(null); }}
        onSave={handleCreateSprint}
        projectId={selectedProject?.id}
        editSprint={editSprint}
      />

      <CreateTaskModal
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        onSave={handleCreateTask}
        projectId={selectedProject?.id}
        sprintId={selectedSprint?.id}
      />

      {showImporter && (
        <DocumentImporter
          onClose={() => setShowImporter(false)}
          onViewProject={(projectId) => {
            setShowImporter(false);
            projectManager.refreshProjects().then(() => {
              const p = projectManager.getAllProjects().find(proj => proj.id === projectId);
              if (p) handleSelectProject(p);
            }).catch(() => {});
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDeleteProject(deleteConfirm.id)}
        itemType="project"
        itemName={deleteConfirm?.name || ''}
        warningText="This will permanently delete the project and all its tasks."
      />
    </div>
  );
}

// ── Sidebar Item ──────────────────────────────────────────────────────────────

function SidebarItem({ icon, label, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
        background: active ? `${NAVY}10` : 'transparent', marginBottom: 1,
        fontFamily: FONT,
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{
        fontSize: 13, fontWeight: active ? 700 : 500, color: NAVY,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    </div>
  );
}

// ── Portfolio Landing ─────────────────────────────────────────────────────────

function PortfolioLanding({ projects, landingView, onSetLandingView, onSelectProject, onNewProject, onImportDocument, onNavigate, isMobile }) {
  useAutoTour('projects');
  const [sortBy, setSortBy] = useState('status');
  const [typeFilter, setTypeFilter] = useState('client');
  const [statusFilter, setStatusFilter] = useState(null);
  const listRef = useRef(null);
  const allTasks = useMemo(() => projectManager.getAllTasks(), []);

  const totalStats = useMemo(() => {
    const active    = projects.filter(p => p.status === 'active').length;
    const outstanding = allTasks.filter(t => t.status !== 'completed').length;
    const overdue   = allTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
    const hoursLogged = Math.round(allTasks.reduce((s, t) => s + ((t.timeTracked || []).reduce((a, e) => a + e.minutes, 0)), 0) / 60);
    return { active, outstanding, overdue, hoursLogged };
  }, [projects, allTasks]);

  const typeFiltered = useMemo(() => projects.filter(p => {
    const type = p.projectType || p.project_type;
    if (typeFilter === 'internal') {
      if (!(type === 'internal' || (!type && !p.client && !p.clientId))) return false;
    } else {
      if (type === 'internal') return false;
    }
    if (statusFilter && p.status !== statusFilter) return false;
    return true;
  }), [projects, typeFilter, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...typeFiltered];
    if (sortBy === 'name')     return list.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'deadline') return list.sort((a, b) => (a.deadline || '9999') < (b.deadline || '9999') ? -1 : 1);
    // Default: active first
    return list.sort((a, b) => {
      const order = { active: 0, 'on hold': 1, completed: 2 };
      return (order[a.status] ?? 3) - (order[b.status] ?? 3);
    });
  }, [typeFiltered, sortBy]);

  return (
    <div style={{ padding: isMobile ? '12px 16px' : '24px 28px', boxSizing: 'border-box', maxWidth: 1100, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h1 style={{ margin: 0, fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 800, color: NAVY }}>
              Projects
            </h1>
            <HelpButton tourId="projects" />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
            {sorted.length} project{sorted.length !== 1 ? 's' : ''} in your workspace
          </p>
        </div>
        <div data-tour-id="projects-view-toggle" style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 8,
          alignItems: isMobile ? 'stretch' : 'center',
          width: isMobile ? '100%' : undefined,
        }}>
          {/* Row 1: View toggle + Sort */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {landingView === 'list' && (
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  padding: '6px 10px', borderRadius: 8, border: `1px solid ${BORDER}`,
                  fontSize: 12, background: '#fff', cursor: 'pointer', fontFamily: FONT,
                  flex: isMobile ? 1 : undefined,
                }}
              >
                <option value="status">Sort: Status</option>
                <option value="name">Sort: Name</option>
                <option value="deadline">Sort: Due Date</option>
              </select>
            )}
            <div style={{ display: 'flex', border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
              {[
                { key: 'grid', label: isMobile ? '⊞' : '⊞ Grid' },
                { key: 'list', label: isMobile ? '≡' : '≡ List' },
              ].map(v => (
                <button key={v.key} onClick={() => onSetLandingView(v.key)}
                  style={{
                    padding: isMobile ? '10px 18px' : '6px 12px', border: 'none',
                    background: landingView === v.key ? NAVY : '#fff',
                    color: landingView === v.key ? '#fff' : '#888',
                    fontWeight: landingView === v.key ? 700 : 400,
                    fontSize: isMobile ? 16 : 12, cursor: 'pointer', fontFamily: FONT,
                    borderRight: v.key === 'grid' ? `1px solid ${BORDER}` : 'none',
                    minHeight: isMobile ? 44 : undefined,
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {/* Row 2 (mobile stacks below) / inline (desktop): Import + New Project */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onImportDocument}
              style={{
                padding: isMobile ? '0 14px' : '8px 14px',
                minHeight: isMobile ? 44 : undefined,
                flex: isMobile ? 1 : undefined,
                borderRadius: 8, border: `1px solid ${NAVY}`,
                background: '#fff', color: NAVY, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              {isMobile ? '📄 Import' : '📄 Import from document'}
            </button>
            <button
              data-tour-id="projects-new-project"
              onClick={onNewProject}
              style={{
                padding: isMobile ? '0 16px' : '8px 16px',
                minHeight: isMobile ? 44 : undefined,
                flex: isMobile ? 1 : undefined,
                borderRadius: 8, border: 'none',
                background: NAVY, color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              + New Project
            </button>
          </div>
        </div>
      </div>

      {/* Type filter tabs */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          display: isMobile ? 'flex' : 'inline-flex',
          background: '#f3f4f6',
          borderRadius: 8,
          padding: 4,
        }}>
          {[
            { key: 'client',   label: 'Client Projects'   },
            { key: 'internal', label: 'Internal Projects' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setTypeFilter(tab.key)}
              onMouseEnter={e => { if (typeFilter !== tab.key) e.currentTarget.style.color = '#1a1a1a'; }}
              onMouseLeave={e => { if (typeFilter !== tab.key) e.currentTarget.style.color = '#6b7280'; }}
              style={{
                flex: isMobile ? 1 : undefined,
                padding: isMobile ? '10px 8px' : '8px 20px',
                borderRadius: 6,
                border: 'none',
                fontSize: isMobile ? 13 : 14,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: FONT,
                transition: 'all 0.15s',
                background: typeFilter === tab.key ? '#ffffff' : 'transparent',
                color: typeFilter === tab.key ? '#1a1a1a' : '#6b7280',
                boxShadow: typeFilter === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                minHeight: isMobile ? 44 : undefined,
                textAlign: 'center',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats bar — 3 clickable tiles (filter / navigate shortcuts) */}
      <div style={isMobile
        ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }
        : { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }
      }>
        {[
          {
            label: 'Active Projects',
            value: totalStats.active,
            color: GREEN,
            onClick: () => {
              setStatusFilter(f => f === 'active' ? null : 'active');
              setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            },
          },
          {
            label: 'Overdue Tasks',
            value: totalStats.overdue,
            color: totalStats.overdue > 0 ? RED : '#aaa',
            onClick: () => onNavigate && onNavigate('Tasks'),
          },
          {
            label: 'Hours Logged',
            value: `${totalStats.hoursLogged}h`,
            color: ACCENT,
            onClick: () => onNavigate && onNavigate('Timesheet'),
          },
        ].map(s => (
          <div
            key={s.label}
            onClick={s.onClick}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            style={{
              flex: isMobile ? undefined : '1 1 140px',
              background: '#fff', border: `1px solid ${BORDER}`,
              borderRadius: isMobile ? 8 : 12,
              padding: isMobile ? '8px 6px' : '14px 18px',
              textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s ease',
            }}
          >
            <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: isMobile ? 9 : 11, color: '#888', marginTop: 2, lineHeight: 1.3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Project list anchor — used for scroll-to on stat card clicks */}
      <div ref={listRef} />

      {/* Empty state */}
      {sorted.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#bbb' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>📁</div>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#aaa', margin: '0 0 6px' }}>No projects yet</p>
          <p style={{ fontSize: 13, margin: '0 0 20px' }}>Create your first project to get started.</p>
          <button
            onClick={onNewProject}
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: NAVY, color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', fontFamily: FONT,
            }}
          >
            + New Project
          </button>
        </div>
      )}

      {/* GRID VIEW */}
      {landingView === 'grid' && sorted.length > 0 && (
        <div data-tour-id="projects-list" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {sorted.map(p => (
            <ProjectCard key={p.id} project={p} onOpen={onSelectProject} />
          ))}
        </div>
      )}

      {/* LIST VIEW */}
      {landingView === 'list' && sorted.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 120px 90px 120px 80px',
            padding: '10px 18px', background: BG,
            borderBottom: `1px solid ${BORDER}`,
            fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            <div>Project</div>
            <div>Client / Category</div>
            <div>Status</div>
            <div>Progress</div>
            <div>Due Date</div>
            <div>Tasks</div>
          </div>
          {sorted.map((p, i) => {
            const stats = projectManager.getProjectStats(p.id);
            const pct = stats.totalTasks > 0 ? Math.round(stats.completedTasks / stats.totalTasks * 100) : 0;
            const isOverdue = p.deadline && new Date(p.deadline) < new Date() && p.status === 'active';
            const isInternal = (p.projectType || p.project_type) === 'internal';
            const clientLabel = isInternal ? (p.category || 'Internal') : (p.client || '—');
            return (
              <div
                key={p.id}
                onClick={() => onSelectProject(p)}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 120px 90px 120px 80px',
                  padding: '12px 18px',
                  borderBottom: `1px solid ${BORDER}`,
                  alignItems: 'center', cursor: 'pointer',
                  background: i % 2 === 0 ? '#fff' : '#fafafa',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color || '#aaa', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{p.name}</span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '1px 5px', borderRadius: 4,
                    background: isInternal ? '#e8f4fd' : '#fff3e0',
                    color: isInternal ? '#2980b9' : '#e67e22',
                    border: `1px solid ${isInternal ? '#b3d9f2' : '#f0c070'}`,
                  }}>
                    {isInternal ? 'Internal' : 'Client'}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>{clientLabel}</div>
                <div><ProjectStatusBadge status={p.status} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ProgressBar pct={pct} color={pct === 100 ? GREEN : p.color} />
                  <span style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap' }}>{pct}%</span>
                </div>
                <div style={{ fontSize: 12, color: isOverdue ? RED : '#666', fontWeight: isOverdue ? 700 : 400 }}>
                  {p.deadline ? fmtDate(p.deadline) : '—'}
                  {isOverdue && ' ⚠'}
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  {stats.completedTasks}/{stats.totalTasks}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Project Card (grid) ───────────────────────────────────────────────────────

function ProjectCard({ project, onOpen }) {
  const stats = useMemo(() => projectManager.getProjectStats(project.id), [project.id]);
  const pct = stats.totalTasks > 0 ? Math.round(stats.completedTasks / stats.totalTasks * 100) : 0;
  const isOverdue = project.deadline && new Date(project.deadline) < new Date() && project.status === 'active';
  const daysLeft = project.deadline
    ? Math.round((new Date(project.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const barColor = pct === 100 ? GREEN : isOverdue ? RED : (project.color || ACCENT);
  const isInternal = (project.projectType || project.project_type) === 'internal';
  const subtitle = isInternal ? (project.category || 'Internal') : project.client;

  return (
    <div
      onClick={() => onOpen(project)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.09)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      style={{
        background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14,
        padding: '18px 20px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 14,
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 12, height: 12, borderRadius: '50%',
          background: project.color || '#aaa', flexShrink: 0, marginTop: 3,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>
              {project.name}
            </span>
            <span style={{
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
              padding: '2px 6px', borderRadius: 4,
              background: isInternal ? '#e8f4fd' : '#fff3e0',
              color: isInternal ? '#2980b9' : '#e67e22',
              border: `1px solid ${isInternal ? '#b3d9f2' : '#f0c070'}`,
              flexShrink: 0,
            }}>
              {isInternal ? 'Internal' : 'Client'}
            </span>
          </div>
          {subtitle && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <ProjectStatusBadge status={project.status} />
      </div>

      {/* Progress */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
          <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>Progress</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: pct === 100 ? GREEN : NAVY }}>{pct}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ProgressBar pct={pct} color={barColor} />
          <span style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap' }}>
            {stats.completedTasks}/{stats.totalTasks}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Avatars */}
        <div style={{ display: 'flex', paddingLeft: 6 }}>
          {(project.team || []).slice(0, 5).map(uid => (
            <Avatar key={uid} userId={uid} size={26} />
          ))}
          {(project.team || []).length > 5 && (
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              background: BORDER, color: '#888',
              fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #fff', marginLeft: -6,
            }}>
              +{(project.team || []).length - 5}
            </div>
          )}
        </div>
        {project.deadline && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            color: isOverdue ? RED : (daysLeft !== null && daysLeft <= 7 ? YELLOW : '#888'),
          }}>
            {isOverdue
              ? `⚠ ${Math.abs(daysLeft)}d overdue`
              : daysLeft === 0 ? '⚡ Due today'
              : daysLeft !== null && daysLeft > 0 ? `${daysLeft}d left`
              : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ project, tasks, allUsers, onEdit }) {
  const stats = useMemo(() => projectManager.getProjectStats(project.id), [project.id]);
  const pct = stats.totalTasks > 0 ? Math.round(stats.completedTasks / stats.totalTasks * 100) : 0;
  const hoursLogged = Math.round(
    tasks.reduce((s, t) => s + ((t.timeTracked || []).reduce((a, e) => a + e.minutes, 0)), 0) / 60 * 10
  ) / 10;
  const isInternal = (project.projectType || project.project_type) === 'internal';

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Project details */}
        <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '20px 22px' }}>
          <h3 style={{ margin: '0 0 16px', fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 800, color: NAVY }}>
            Project Details
          </h3>
          {[
            ['Status',    <ProjectStatusBadge status={project.status} />],
            ['Type',      isInternal ? 'Internal' : 'Client'],
            isInternal
              ? ['Category',   project.category || '—']
              : ['Client',     project.client || '—'],
            ['Start date', project.startDate ? fmtDate(project.startDate) : '—'],
            ['Due date',  project.deadline ? fmtDate(project.deadline) : '—'],
            ...(!isInternal ? [['Budget', project.budget ? `£${Number(project.budget).toLocaleString()}` : '—']] : []),
          ].map(([label, value]) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '9px 0', borderBottom: `1px solid ${BG}`,
            }}>
              <span style={{ fontSize: 13, color: '#888', fontWeight: 600 }}>{label}</span>
              <span style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>
                {typeof value === 'string' ? value : value}
              </span>
            </div>
          ))}
          {project.description && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Description
              </div>
              <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0 }}>
                {project.description}
              </p>
            </div>
          )}
          <button
            onClick={onEdit}
            style={{
              marginTop: 18, padding: '7px 16px', borderRadius: 8,
              border: `1px solid ${BORDER}`, background: '#fff', color: NAVY,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
            }}
          >
            Edit Project
          </button>
        </div>

        {/* Progress & stats */}
        <div>
          <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 14px', fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 800, color: NAVY }}>
              Progress
            </h3>
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: '#888' }}>Task completion</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: pct === 100 ? GREEN : NAVY }}>{pct}%</span>
              </div>
              <ProgressBar pct={pct} color={pct === 100 ? GREEN : (project.color || ACCENT)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
              {[
                { label: 'Total tasks',    value: stats.totalTasks,      color: NAVY  },
                { label: 'Completed',      value: stats.completedTasks,  color: GREEN },
                { label: 'In progress',    value: stats.inProgressTasks, color: ACCENT },
                { label: 'Hours logged',   value: `${hoursLogged}h`,     color: '#5352ed' },
              ].map(s => (
                <div key={s.label} style={{ background: BG, borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Team */}
          {(project.team || []).length > 0 && (
            <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '18px 22px' }}>
              <h3 style={{ margin: '0 0 12px', fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 800, color: NAVY }}>
                Team
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(project.team || []).map(uid => {
                  const u = allUsers.find(usr => usr.id === uid);
                  if (!u) return null;
                  return (
                    <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar userId={uid} size={32} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{u.name}</div>
                        {u.jobTitle && <div style={{ fontSize: 11, color: '#888' }}>{u.jobTitle}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Task List View ────────────────────────────────────────────────────────────

function TaskListView({ tasks, sections, selectedProject, allUsers, onTaskClick, onStatusChange,
  onAddSection, showAddSection, setShowAddSection, newSectionName, setNewSectionName }) {

  const grouped = useMemo(() => {
    const groups = [];
    if (sections.length > 0) {
      sections.forEach(sec => {
        groups.push({ id: sec.id, name: sec.name, tasks: tasks.filter(t => t.sectionId === sec.id) });
      });
    }
    const unsectioned = tasks.filter(t => !t.sectionId || !sections.find(s => s.id === t.sectionId));
    if (unsectioned.length > 0 || sections.length === 0) {
      groups.push({ id: null, name: sections.length > 0 ? 'No Section' : '', tasks: unsectioned });
    }
    return groups;
  }, [tasks, sections]);

  return (
    <div>
      {grouped.map(group => (
        <div key={group.id ?? 'none'} style={{ marginBottom: 20 }}>
          {group.name && (
            <div style={{
              fontSize: 12, fontWeight: 700, color: '#888',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              padding: '0 0 8px 0', borderBottom: `1px solid ${BORDER}`, marginBottom: 4,
            }}>
              {group.name} ({group.tasks.length})
            </div>
          )}
          <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 100px 100px 110px 100px 70px',
              padding: '8px 14px', background: BG,
              borderBottom: `1px solid ${BORDER}`,
              fontSize: 11, fontWeight: 700, color: '#aaa',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              <div>Task</div>
              <div>Assignee</div>
              <div>Due Date</div>
              <div>Priority</div>
              <div>Status</div>
              <div>Time</div>
            </div>
            {group.tasks.length === 0 ? (
              <div style={{ padding: '20px 14px', color: '#ccc', fontSize: 13, textAlign: 'center' }}>No tasks</div>
            ) : (
              group.tasks.map(task => {
                const totalMins = (task.timeTracked || []).reduce((s, e) => s + e.minutes, 0);
                const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
                return (
                  <div
                    key={task.id}
                    onClick={() => onTaskClick(task.id)}
                    onMouseEnter={e => e.currentTarget.style.background = BG}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 100px 100px 110px 100px 70px',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${BORDER}`,
                      alignItems: 'center', cursor: 'pointer',
                      background: '#fff', transition: 'background 0.1s',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, color: NAVY }}>{task.title}</div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {(task.assignedTo || []).slice(0, 3).map(uid => (
                        <Avatar key={uid} userId={uid} size={24} />
                      ))}
                      {(task.assignedTo || []).length > 3 && (
                        <span style={{ fontSize: 10, color: '#aaa', alignSelf: 'center', marginLeft: 2 }}>
                          +{(task.assignedTo || []).length - 3}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: isOverdue ? RED : '#666', fontWeight: isOverdue ? 700 : 400 }}>
                      {task.dueDate ? fmtDate(task.dueDate) : '—'}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <PriorityBadge priority={task.priority} />
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <StatusBadge status={task.status} onChange={v => onStatusChange(task.id, v)} />
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa' }}>{formatTime(totalMins)}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ))}

      {/* Add Section */}
      {selectedProject && (
        <div style={{ marginTop: 8 }}>
          {showAddSection ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                autoFocus value={newSectionName}
                onChange={e => setNewSectionName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onAddSection(); if (e.key === 'Escape') setShowAddSection(false); }}
                placeholder="Section name…"
                style={{ flex: 1, padding: '7px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, outline: 'none', fontFamily: FONT }}
              />
              <button onClick={onAddSection} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Add</button>
              <button onClick={() => setShowAddSection(false)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowAddSection(true)} style={{ padding: '7px 14px', borderRadius: 8, border: `1px dashed ${BORDER}`, background: 'transparent', fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
              + Add Section
            </button>
          )}
        </div>
      )}

      {tasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#bbb' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#aaa', marginBottom: 4 }}>No tasks yet</p>
          <p style={{ fontSize: 13 }}>Click "+ Add Task" to get started.</p>
        </div>
      )}
    </div>
  );
}

// ── Kanban View (4 columns) ───────────────────────────────────────────────────

const LEGACY_KANBAN_COLUMNS = [
  { id: null, key: 'todo',        label: 'To Do',       colour: '#a4b0be' },
  { id: null, key: 'in progress', label: 'In Progress',  colour: ACCENT   },
  { id: null, key: 'review',      label: 'Review',       colour: '#f9ca24' },
  { id: null, key: 'completed',   label: 'Completed',    colour: GREEN    },
];

// Maps legacy status strings to stage names for fallback bucketing
const STATUS_TO_STAGE = {
  'todo': 'To Do',
  'in progress': 'In Progress',
  'review': 'Review',
  'completed': 'Completed',
};

function KanbanView({ tasks, stages = [], allUsers, onTaskClick, onDragStart, onDrop, draggingId }) {
  // Use dynamic stages if available, fall back to legacy columns
  const columns = stages.length > 0
    ? stages.map(s => ({ id: s.id, key: s.id, label: s.name, colour: s.colour || '#94a3b8' }))
    : LEGACY_KANBAN_COLUMNS;

  // Tasks with no stageId fall into the first isStart stage (or stages[0])
  const startStageId = stages.length > 0
    ? (stages.find(s => s.isStart)?.id ?? stages[0]?.id ?? null)
    : null;

  return (
    <div style={{
      display: 'flex', gap: 14, padding: '16px 24px',
      height: '100%', overflowX: 'auto', alignItems: 'flex-start',
      boxSizing: 'border-box',
    }}>
      {columns.map(col => {
        const colTasks = stages.length > 0
          ? tasks.filter(t => {
              if (t.stageId) return t.stageId === col.id;
              return col.id === startStageId;
            })
          : tasks.filter(t => t.status === col.key);
        return (
          <div
            key={col.key}
            onDragOver={e => e.preventDefault()}
            onDrop={e => onDrop(e, col.id ? col : col.key)}
            style={{
              flex: '0 0 280px', width: 280,
              background: '#ffffff', borderRadius: 12,
              border: `1px solid ${BORDER}`,
              padding: '12px 10px',
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Column header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.colour, flexShrink: 0 }} />
              <span style={{
                fontSize: 12, fontWeight: 700, color: NAVY,
                textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1,
              }}>
                {col.label}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#aaa',
                background: '#fff', border: `1px solid ${BORDER}`,
                borderRadius: 10, padding: '1px 7px',
              }}>
                {colTasks.length}
              </span>
            </div>

            {/* Cards */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {colTasks.map(task => (
                <KanbanCard
                  key={task.id}
                  task={task}
                  allUsers={allUsers}
                  isDragging={draggingId === task.id}
                  onDragStart={onDragStart}
                  onClick={() => onTaskClick(task.id)}
                />
              ))}
              {colTasks.length === 0 && (
                <div style={{
                  padding: '24px 0', textAlign: 'center', color: '#ccc',
                  fontSize: 12, border: `2px dashed ${BORDER}`, borderRadius: 8,
                }}>
                  Drop tasks here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ task, allUsers, isDragging, onDragStart, onClick }) {
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const totalMins = (task.timeTracked || []).reduce((s, e) => s + e.minutes, 0);
  const priorityColor = PRIORITY_COLOR[task.priority] || '#ddd';

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, task.id)}
      onClick={onClick}
      onMouseEnter={e => { if (!isDragging) e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
      style={{
        background: isDragging ? BG : '#fff',
        border: `1px solid ${BORDER}`,
        borderLeft: `3px solid ${priorityColor}`,
        borderRadius: 10, padding: '10px 12px', marginBottom: 8,
        cursor: 'grab', opacity: isDragging ? 0.5 : 1,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'opacity 0.15s, box-shadow 0.15s',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 8, lineHeight: 1.35 }}>
        {task.title}
      </div>

      {(task.tags || []).length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
          {task.tags.slice(0, 2).map(tag => (
            <span key={tag} style={{
              fontSize: 10, background: `${ACCENT}15`, color: ACCENT,
              padding: '1px 6px', borderRadius: 8, fontWeight: 600,
            }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PriorityBadge priority={task.priority} />
        <div style={{ display: 'flex' }}>
          {(task.assignedTo || []).slice(0, 3).map(uid => (
            <Avatar key={uid} userId={uid} size={22} />
          ))}
        </div>
      </div>

      {(task.dueDate || totalMins > 0) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}`,
          fontSize: 11, color: '#aaa',
        }}>
          {task.dueDate && (
            <span style={{ color: isOverdue ? RED : '#aaa', fontWeight: isOverdue ? 700 : 400 }}>
              {isOverdue ? '⚠ ' : ''}{fmtDate(task.dueDate)}
            </span>
          )}
          {totalMins > 0 && <span>⏱ {formatTime(totalMins)}</span>}
        </div>
      )}
    </div>
  );
}

// ── By Priority View ──────────────────────────────────────────────────────────

function PriorityView({ tasks, allUsers, onTaskClick, onStatusChange }) {
  const groups = useMemo(() => {
    const order = ['Critical', 'High', 'Medium', 'Low'];
    return order.map(p => ({ priority: p, tasks: tasks.filter(t => t.priority === p) }))
      .filter(g => g.tasks.length > 0);
  }, [tasks]);

  if (tasks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <p style={{ fontSize: 14, color: '#aaa' }}>No tasks yet</p>
      </div>
    );
  }

  return (
    <div>
      {groups.map(group => {
        const color = PRIORITY_COLOR[group.priority] || '#ddd';
        return (
          <div key={group.priority} style={{ marginBottom: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 0 8px 0', borderBottom: `2px solid ${color}40`, marginBottom: 4,
            }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 12, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {group.priority}
              </span>
              <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>({group.tasks.length})</span>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
              {group.tasks.map(task => {
                const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
                const totalMins = (task.timeTracked || []).reduce((s, e) => s + e.minutes, 0);
                return (
                  <div
                    key={task.id}
                    onClick={() => onTaskClick(task.id)}
                    onMouseEnter={e => e.currentTarget.style.background = BG}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', borderBottom: `1px solid ${BORDER}`,
                      cursor: 'pointer', background: '#fff', transition: 'background 0.1s',
                    }}
                  >
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: NAVY }}>{task.title}</div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {(task.assignedTo || []).slice(0, 3).map(uid => <Avatar key={uid} userId={uid} size={22} />)}
                    </div>
                    <div style={{ fontSize: 12, color: isOverdue ? RED : '#aaa', fontWeight: isOverdue ? 700 : 400, whiteSpace: 'nowrap' }}>
                      {task.dueDate ? fmtDate(task.dueDate) : '—'}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <StatusBadge status={task.status} onChange={v => onStatusChange(task.id, v)} />
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', minWidth: 36 }}>{formatTime(totalMins)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Files Tab ─────────────────────────────────────────────────────────────────

function ProjFileIcon({ source, mimeType }) {
  if (source === 'google_drive') return (
    <svg width="18" height="18" viewBox="0 0 87.3 78" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5L6.6 66.85z" fill="#0066da"/>
      <path d="M43.65 25L29.9 1.2C28.55.4 27 0 25.45 0c-1.55 0-3.1.4-4.5 1.2L6.6 11.15 27.5 47h32.3L43.65 25z" fill="#00ac47"/>
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l6.45 12.4 7.3 11.4z" fill="#ea4335"/>
      <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.95 0H34.35c-1.55 0-3.1.4-4.45 1.2L43.65 25z" fill="#00832d"/>
      <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.45 1.2h50.9c1.55 0 3.1-.4 4.45-1.2L59.8 53z" fill="#2684fc"/>
      <path d="M73.4 27.5L59.65 3.85C58.85 2.45 57.75 1.35 56.4.55L43.65 25l16.15 28H87.3c0-1.55-.4-3.1-1.2-4.5L73.4 27.5z" fill="#ffba00"/>
    </svg>
  );
  if (source === 'onedrive') return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.5 11.5a4.5 4.5 0 0 0-4.25-4.49A6 6 0 0 0 3.75 10.5a3.75 3.75 0 0 0 .75 7.5h15a3 3 0 0 0 0-6.5z" fill="#0078d4"/>
    </svg>
  );
  if (source === 'url') return <span style={{ fontSize: 16 }}>🔗</span>;
  if (mimeType?.startsWith('image/')) return <span style={{ fontSize: 18 }}>🖼️</span>;
  if (mimeType === 'application/pdf') return <span style={{ fontSize: 18 }}>📄</span>;
  return <span style={{ fontSize: 18 }}>📎</span>;
}

function FilesTab({ project }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingFile, setAddingFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkingFile, setLinkingFile] = useState(false);
  const [linkSource, setLinkSource] = useState('google_drive');
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  useEffect(() => {
    api(`/api/projects/${project.id}/files`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.files)) setFiles(d.files); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  async function addFile() {
    if (!newFileName.trim() || !newFileUrl.trim()) return;
    setSaving(true);
    try {
      const res = await api(`/api/projects/${project.id}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: newFileName.trim(), fileUrl: newFileUrl.trim(), source: 'upload' }),
      });
      const data = await res.json();
      if (data.file) {
        setFiles(prev => [data.file, ...prev]);
        setNewFileName('');
        setNewFileUrl('');
        setAddingFile(false);
      }
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  }

  async function addLink() {
    if (!linkName.trim() || !linkUrl.trim()) return;
    setSavingLink(true);
    try {
      const res = await api(`/api/projects/${project.id}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: linkName.trim(), externalUrl: linkUrl.trim(), source: linkSource }),
      });
      const data = await res.json();
      if (data.file) {
        setFiles(prev => [data.file, ...prev]);
        setLinkName('');
        setLinkUrl('');
        setLinkingFile(false);
      }
    } catch { /* ignore */ } finally {
      setSavingLink(false);
    }
  }

  async function removeFile(fileId) {
    await api(`/api/projects/${project.id}/files/${fileId}`, { method: 'DELETE' });
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }

  if (loading) return <div style={{ padding: '32px 28px', color: '#aaa', fontSize: 13 }}>Loading…</div>;

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {files.map(f => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff' }}>
          <ProjFileIcon source={f.source} mimeType={f.mimeType} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {f.source && f.source !== 'upload' && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: f.source === 'google_drive' ? '#4285f4' : f.source === 'onedrive' ? '#0078d4' : '#888', borderRadius: 3, padding: '1px 5px' }}>
                  {f.source === 'google_drive' ? 'Google Drive' : f.source === 'onedrive' ? 'OneDrive' : 'Link'}
                </span>
              )}
              <span style={{ fontSize: 11, color: '#aaa' }}>{new Date(f.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
          <a href={f.externalUrl || f.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: ACCENT, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            {f.source && f.source !== 'upload' ? 'Open' : 'Download'}
          </a>
          <button onClick={() => removeFile(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18, lineHeight: 1, padding: 2 }} title="Remove">×</button>
        </div>
      ))}
      {files.length === 0 && !addingFile && !linkingFile && (
        <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>No files attached to this project yet.</p>
      )}
      {addingFile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', borderRadius: 8, border: `1px dashed ${BORDER}`, background: BG }}>
          <input value={newFileName} onChange={e => setNewFileName(e.target.value)} placeholder="File name (e.g. Creative Brief.pdf)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
          <input value={newFileUrl} onChange={e => setNewFileUrl(e.target.value)} placeholder="File URL (S3, Dropbox…)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={addFile} disabled={saving || !newFileName.trim() || !newFileUrl.trim()} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setAddingFile(false); setNewFileName(''); setNewFileUrl(''); }} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
      {linkingFile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', borderRadius: 8, border: `1px dashed #4285f4`, background: '#f0f4ff' }}>
          <select value={linkSource} onChange={e => setLinkSource(e.target.value)} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
            <option value="google_drive">Google Drive</option>
            <option value="onedrive">OneDrive</option>
            <option value="url">Other URL</option>
          </select>
          <input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="Display name (e.g. Brand Guidelines)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
          <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Paste link (must start with https://)" style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={addLink} disabled={savingLink || !linkName.trim() || !linkUrl.trim()} style={{ background: '#4285f4', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: savingLink ? 0.7 : 1 }}>
              {savingLink ? 'Linking…' : 'Link'}
            </button>
            <button onClick={() => { setLinkingFile(false); setLinkName(''); setLinkUrl(''); }} style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
      {!addingFile && !linkingFile && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setAddingFile(true)} style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 7, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: NAVY }}>
            + Attach file
          </button>
          <button onClick={() => setLinkingFile(true)} style={{ background: '#f0f4ff', border: `1px solid #c5d5f5`, borderRadius: 7, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#4285f4' }}>
            📎 Link file
          </button>
        </div>
      )}
    </div>
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────────

function NotesTab({ project }) {
  const [notes, setNotes] = useState(project.notes || '');
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);

  // Load latest notes from DB on mount
  useEffect(() => {
    api(`/api/projects/${project.id}/notes`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.notes != null) setNotes(d.notes); })
      .catch(() => {});
  }, [project.id]);

  const handleChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    setSaved(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      api(`/api/projects/${project.id}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ notes: val }),
      }).then(() => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }).catch(() => {});
    }, 800);
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontFamily: "'Poppins', sans-serif", fontSize: 15, fontWeight: 800, color: NAVY }}>
          Project Notes
        </h3>
        {saved && (
          <span style={{ fontSize: 12, color: GREEN, fontWeight: 600 }}>✓ Saved</span>
        )}
      </div>
      <textarea
        value={notes}
        onChange={handleChange}
        placeholder={`Add notes for ${project.name}…\n\nMeeting notes, decisions, ideas, anything relevant to this project.`}
        style={{
          width: '100%', minHeight: 400, padding: '14px 16px',
          border: `1px solid ${BORDER}`, borderRadius: 10,
          fontSize: 14, fontFamily: FONT, color: '#333',
          lineHeight: 1.7, resize: 'vertical', outline: 'none',
          boxSizing: 'border-box', background: '#fff',
        }}
      />
      <p style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>
        Notes are auto-saved and synced across devices.
      </p>
    </div>
  );
}
